import {
  AspectRatio,
  Card,
  Heading,
  Stack,
  Inline,
  Box,
  Icon,
  AbsolutePosition,
  Badge,
  Skeleton
} from '@front10/base-elements';
import * as React from 'react';
import ItemPrice from '../_commons/atoms/ItemPrice';
import { EventImage } from './EventImage';
import { useTranslation } from '@front10/utils';

interface Props {
  offerLabel?: string;
  title: string;
  date: string;
  sessionType: string;
  price: string;
  imageUrl: string;
  locationName: string;
  ctaUrl: string;
  badgeLabel?: string;
  showBadge?: boolean;
  badgeVariant?: string;
  ctaLabel?: string;
  icon?: string;
  width?: string;
  ctaSlot?: React.ReactElement | null;
  moreInfoSlot?: React.ReactElement | null;
  counter?: string;
  tableAvailability?: string[];
  cardSettings?: {
    showEventName?: boolean;
    showEventDate?: boolean;
    showEventSession?: boolean;
    showEventArena?: boolean;
    showTableSize?: boolean;
    showMoreInfoLink?: boolean;
    showPrice?: boolean;
    showCta?: boolean;
    showSeparator?: boolean;
  };
  componentName?: string;
}

export function EventCard({
  title,
  width,
  price,
  imageUrl,
  sessionType,
  date,
  locationName,
  badgeLabel,
  showBadge = false,
  badgeVariant,
  offerLabel,
  ctaSlot = null,
  moreInfoSlot = null,
  counter,
  tableAvailability,
  cardSettings = {},
  componentName
}: Props) {
  const {
    showEventName = true,
    showEventDate = true,
    showEventSession = true,
    showEventArena = true,
    showTableSize = true,
    showMoreInfoLink = true,
    showPrice = true,
    showCta = true,
    showSeparator = true
  } = cardSettings;
  const translate = useTranslation();
  return (
    <Card
      sx={{
        width,
        minHeight: '495px',
        height: '100%',
        '[data-fr-item-price]': {
          '[data-fr-promo-label],[data-fr-extra-info]': {
            fontFamily: 'heading',
            fontSize: '9px',
            lineHeight: 2,
            fontWeight: 500
          },
          '[data-fr-currency]': {
            fontSize: '22px',
            lineHeight: 1,
            fontWeight: 700,
            color: 'accent'
          }
        }
      }}
      variant="primary"
      data-fr-event-card
    >
      <Skeleton variant="rectangular" width="100%">
        <Box sx={{ position: 'relative' }}>
          <AspectRatio ratio={16 / 10}>
            <EventImage altText="" image={imageUrl} />
          </AspectRatio>
          {counter ? (
            <AbsolutePosition right="10px" top="10px">
              <Badge variant="pill">{counter}</Badge>
            </AbsolutePosition>
          ) : null}
          {showBadge && badgeLabel ? (
            <AbsolutePosition left="10px" top="12px">
              <Badge variant={badgeVariant}>{badgeLabel}</Badge>
            </AbsolutePosition>
          ) : null}
          <AbsolutePosition left="10px" bottom="10px">
            <Badge>{offerLabel}</Badge>
          </AbsolutePosition>
        </Box>
      </Skeleton>
      <Stack pt="12px" px="10px" pb="15px" alignY="space-between" sx={{ height: '100%' }}>
        <Skeleton>
          {showEventName && (
            <Heading
              data-testid="customid"
              variant="heading2"
              sx={{ minHeight: 'calc(2.5em * 1.3)' }}
            >
              {title}
            </Heading>
          )}
        </Skeleton>
        <Stack mt="14px" gap="1" sx={{ flex: 1 }}>
          <Skeleton>
            <Inline gap="2" alignY="center" variant="text.label">
              {showEventDate && (
                <Inline gap="1">
                  <Icon
                    icon="calendar"
                    size="1.43em"
                    sx={{ flex: '0 0 auto', position: 'relative', top: '0.0625em' }}
                  />
                  <span>{date}</span>
                </Inline>
              )}
              {showSeparator && <span>|</span>}
              {showEventSession && <span>{sessionType}</span>}
            </Inline>
          </Skeleton>
          <Skeleton>
            {showEventArena && (
              <Inline gap="1" variant="text.label">
                <Icon
                  icon="marker"
                  size="1.43em"
                  sx={{ flex: '0 0 auto', position: 'relative', top: '0.0625em' }}
                />
                <span>{locationName}</span>
              </Inline>
            )}
          </Skeleton>
          <Skeleton>
            {tableAvailability && showTableSize ? (
              <Inline gap="1" variant="text.label">
                <Icon
                  icon="avatar"
                  size="1.43em"
                  sx={{ flex: '0 0 auto', position: 'relative', top: '0.0625em' }}
                />
                <Stack gap="1" data-fr-table-availability>
                  <span>
                    {translate(
                      `cmp-${componentName}-table-availability-label`,
                      'Table size availability'
                    )}
                  </span>
                  <Inline gap="4px" wrap={true}>
                    {tableAvailability.map((item) => (
                      <Inline
                        align="center"
                        alignY="center"
                        sx={{
                          borderRadius: '50%',
                          width: '1.45em',
                          height: '1.45em',
                          paddingTop: '4px',
                          border: '1px solid currentColor',
                          '@supports (-webkit-touch-callout: none)': {
                            paddingTop: '0px'
                          }
                        }}
                        key={item}
                      >
                        {item}
                      </Inline>
                    ))}
                  </Inline>
                </Stack>
              </Inline>
            ) : null}
          </Skeleton>
          <Box>
            <Skeleton variant="rectangular" disabled={moreInfoSlot == null ? true : undefined}>
              {showMoreInfoLink && moreInfoSlot}
            </Skeleton>
          </Box>
        </Stack>
        <Box
          sx={{ alignSelf: 'flex-end', marginTop: '-35px', marginRight: '10px', minHeight: '60px' }}
          variant="cards.price"
        >
          <Skeleton>
            {price && showPrice ? (
              <ItemPrice
                formattedTotalPrice={price}
                showCurrencyCode={false}
                showPriceDisclaimer={false}
                showCurrencySymbolSub={false}
                showPriceExtraInfo={true}
              />
            ) : null}
          </Skeleton>
        </Box>
        <Stack pt="6px">
          <Skeleton variant="rectangular" width="100%">
            {showCta && ctaSlot}
          </Skeleton>
        </Stack>
      </Stack>
    </Card>
  );
}
